import api from './api';

const publicBase = import.meta.env.PROD
  ? import.meta.env.VITE_API_URL || 'https://api.landflow.com/api'
  : import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/** Document with optional download URL (included when fetching single application). */
export interface RentalApplicationDocument {
  name: string;
  key: string;
  mimeType: string;
  size: number;
  documentType?: string;
  applicantRef?: string;
  url?: string;
}

export interface RentalApplicationSummary {
  _id: string;
  propertyId: string;
  /** Primary applicant (when using structured application) */
  primaryApplicant?: Record<string, unknown>;
  /** Co-applicants (adults) */
  coApplicants?: Array<{ firstName: string; lastName: string; email?: string; phone?: string; dateOfBirth?: string; [k: string]: unknown }>;
  /** Child occupants */
  childOccupants?: Array<{ fullName: string; dateOfBirth?: string }>;
  /** Supporting documents (full list with url when from getApplication) */
  documents?: RentalApplicationDocument[];
  /** Main applicant (legacy; also kept in sync with primaryApplicant) */
  applicant: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    moveInDate?: string;
    numberOfOccupants?: number;
    currentAddress?: string;
    employment?: string;
    employer?: string;
    income?: number;
    incomeFrequency?: string;
    notes?: string;
  };
  status: string;
  internalNotes?: string;
  submittedAt: string;
  submittedVia: string;
  reviewedAt?: string;
  reviewedBy?: string;
  outcome?: string;
  outcomeNotes?: string;
  ownerOutcome?: string;
  ownerOutcomeNotes?: string;
  /** When the agent confirmed the bond has been lodged */
  bondLodgedAt?: string;
  /** NSW tenancy agreement (when generated for approved application) */
  tenancyAgreement?: {
    key: string;
    fileName: string;
    url?: string;
    generatedAt: string;
    termType?: string;
    startDate?: string;
    endDate?: string;
    weeklyRent?: number;
    bondAmount?: number;
    rentPeriod?: string;
    paymentMethod?: string;
    includedItems?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/** States for which the online rental application form is available (must match backend). */
export const SUPPORTED_RENTAL_APPLICATION_STATES = ['NSW', 'VIC'];

export function isRentalApplicationSupported(state: string | undefined): boolean {
  if (state == null || typeof state !== 'string') return false;
  const s = state.trim().toUpperCase();
  return s.length > 0 && SUPPORTED_RENTAL_APPLICATION_STATES.includes(s);
}

export async function getApplicationLink(
  agencySlug: string,
  propertyId: string
): Promise<{
  token: string;
  applicationUrl: string;
  formSupported: boolean;
  unsupportedMessage?: string;
}> {
  const { data } = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/rental-application-link`);
  return data;
}

export async function listApplications(agencySlug: string, propertyId: string): Promise<RentalApplicationSummary[]> {
  const { data } = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/rental-applications`);
  return data.data || [];
}

export async function getApplication(agencySlug: string, propertyId: string, applicationId: string): Promise<RentalApplicationSummary> {
  const { data } = await api.get(`/agencies/${agencySlug}/properties/${propertyId}/rental-applications/${applicationId}`);
  return data;
}

export async function updateApplication(
  agencySlug: string,
  propertyId: string,
  applicationId: string,
  payload: {
    status?: string;
    internalNotes?: string;
    outcome?: string;
    outcomeNotes?: string;
    ownerOutcome?: string;
    ownerOutcomeNotes?: string;
    bondLodged?: boolean;
  }
): Promise<RentalApplicationSummary> {
  const { data } = await api.patch(
    `/agencies/${agencySlug}/properties/${propertyId}/rental-applications/${applicationId}`,
    payload
  );
  return data;
}

export interface GenerateTenancyAgreementOptions {
  termType?: 'fixed' | 'periodic';
  startDate?: string;
  endDate?: string;
  weeklyRent?: number;
  bondAmount?: number;
  rentPeriod?: 'week' | 'fortnight';
  paymentMethod?: string;
  includedItems?: string;
}

export async function generateTenancyAgreement(
  agencySlug: string,
  propertyId: string,
  applicationId: string,
  options?: GenerateTenancyAgreementOptions
): Promise<{ success: boolean; message: string; tenancyAgreement: { url: string; fileName: string; [k: string]: unknown } }> {
  const { data } = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/rental-applications/${applicationId}/tenancy-agreement`,
    options || {}
  );
  return data;
}

/** Upload signed tenancy agreement (e.g. VIC Form 1/Form 2). PDF or .docx, max 20MB. */
export async function uploadTenancyAgreement(
  agencySlug: string,
  propertyId: string,
  applicationId: string,
  file: File
): Promise<{ success: boolean; message: string; tenancyAgreement: { url: string; fileName: string; [k: string]: unknown } }> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post(
    `/agencies/${agencySlug}/properties/${propertyId}/rental-applications/${applicationId}/tenancy-agreement/upload`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function createApplication(
  agencySlug: string,
  propertyId: string,
  payload: { applicant: Record<string, unknown>; status?: string; internalNotes?: string }
): Promise<RentalApplicationSummary> {
  const { data } = await api.post(`/agencies/${agencySlug}/properties/${propertyId}/rental-applications`, payload);
  return data;
}

export interface FormFieldDef {
  name: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  section?: string;
}

export interface FormSection {
  id: string;
  title: string;
  subtitle?: string;
  /** When true, frontend renders multiple blocks with "Add another" using blockPrefix and index. */
  repeatable?: boolean;
  blockPrefix?: string;
  maxBlocks?: number;
  fields: FormFieldDef[];
}

/** Agency banner for public form branding */
export interface PublicFormBanner {
  bannerImageData?: string;
  bannerImageType?: string;
  bannerTitle?: string;
}

/** Public (no auth) - get form config and property summary by token (agency-scoped) */
export async function getPublicForm(agencySlug: string, token: string): Promise<{
  propertyId: string;
  summary: { address: string; suburb?: string; postcode?: string; bedrooms?: number; bathrooms?: number; description?: string; weeklyRent?: number; bondAmount?: number; dateAvailable?: string };
  formSupported?: boolean;
  unsupportedMessage?: string;
  /** State code when form is supported (e.g. NSW, VIC) for display title */
  state?: string;
  sections?: FormSection[];
  formFields: FormFieldDef[];
  /** Agency banner when available (base64 image + optional title) */
  banner?: PublicFormBanner;
}> {
  const res = await fetch(`${publicBase}/public/${encodeURIComponent(agencySlug)}/rental-application/${encodeURIComponent(token)}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load application form');
  }
  return res.json();
}

/** Document to upload with the application (Section 8 supporting documents) */
export interface PublicFormDocument {
  fileName: string;
  mimeType: string;
  base64: string;
  /** Document type (e.g. "Australian Passport (70 points)", "Payslip 1") */
  documentType: string;
  /** Which applicant this document belongs to: 'primary' | 'coApplicant_0' | 'coApplicant_1' ... */
  applicantRef?: string;
}

/** Payload for public application submit: structured (primary + co-applicants + children) or legacy flat applicant. */
export interface PublicApplicationPayload {
  /** New structure: primary applicant (all section 2 fields + extra form fields) */
  primaryApplicant?: Record<string, unknown>;
  /** Co-applicants (adults) */
  coApplicants?: Array<Record<string, unknown>>;
  /** Child occupants */
  childOccupants?: Array<{ fullName: string; dateOfBirth?: string }>;
  /** Legacy: single applicant object (used when form has no primaryApplicant fields) */
  applicant?: Record<string, unknown>;
}

/** Public (no auth) - submit application by token. Payload only (no documents in body). Documents are uploaded separately via uploadPublicApplicationDocuments. */
export async function submitPublicApplication(
  agencySlug: string,
  token: string,
  payload: PublicApplicationPayload | Record<string, unknown>
): Promise<{ success: boolean; message: string; applicationId: string; applicantAccessToken?: string; applicantAccessCode?: string }> {
  const res = await fetch(`${publicBase}/public/${encodeURIComponent(agencySlug)}/rental-application/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to submit application');
  return data;
}

/** Public (no auth) - upload documents for an existing application (multipart). documentType should be the display label for 100-point parsing (e.g. "Australian Passport (current) – 70 points"). */
export async function uploadPublicApplicationDocuments(
  agencySlug: string,
  token: string,
  applicationId: string,
  documents: Array<{ file: File; documentType: string; applicantRef: string }>
): Promise<{ success: boolean; message: string }> {
  const formData = new FormData();
  formData.append('applicationId', applicationId);
  const labels = documents.map((d) => d.documentType);
  const refs = documents.map((d) => d.applicantRef || 'primary');
  formData.append('documentTypes', JSON.stringify(labels));
  formData.append('applicantRefs', JSON.stringify(refs));
  documents.forEach((d) => formData.append('documents', d.file));
  const res = await fetch(`${publicBase}/public/${encodeURIComponent(agencySlug)}/rental-application/${encodeURIComponent(token)}/documents`, {
    method: 'POST',
    body: formData,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to upload documents');
  return data;
}

/** Public (no auth) - get application by applicant access token + 6-digit code. POST with body { code }. */
export async function getApplicationByApplicantToken(
  applicantToken: string,
  code: string
): Promise<{
  summary: { address: string; suburb?: string; postcode?: string; bedrooms?: number; bathrooms?: number; description?: string; weeklyRent?: number; bondAmount?: number; dateAvailable?: string };
  application: RentalApplicationSummary;
}> {
  const res = await fetch(`${publicBase}/public/rental-application/view/${applicantToken}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.trim() }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error(data.error || 'Access code required');
  if (res.status === 403) throw new Error(data.error || 'Invalid access code');
  if (!res.ok) throw new Error(data.error || 'Failed to load application');
  return data;
}

/** Public (no auth) - update application by applicant token + code. Only allowed when status is submitted or pending_review. */
export async function updateApplicationByApplicantToken(
  applicantToken: string,
  code: string,
  primaryApplicant: Record<string, unknown>
): Promise<{ summary: any; application: RentalApplicationSummary }> {
  const res = await fetch(`${publicBase}/public/rental-application/view/${applicantToken}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.trim(), primaryApplicant }),
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error(data.error || 'Access code required');
  if (res.status === 403) throw new Error(data.error || 'Invalid access code');
  if (res.status === 400) throw new Error(data.error || 'Cannot update application');
  if (!res.ok) throw new Error(data.error || 'Failed to update application');
  return data;
}
